// # Main Signup Component (Step 1)
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../auth/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import AuthHeader from '../../components/AuthHeader';
import { validateEmail } from '../../../utils/emails';
import './styles/common.css';
import './styles/main.css';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../../auth/firebase';
import LoadingSpinner from '../../components/LoadingSpinner';
import signupImage from '../../../aneo_media/signup/signup.jpg';

function SignupMain() {
  const navigate = useNavigate();
  const formSectionRef = useRef(null); // # Add ref for form section
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    emailError: '',
    passwordError: '',
    confirmPasswordError: ''
  });
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(
    parseInt(sessionStorage.getItem('signupAttempts') || '0')
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      const { isValid, message } = validateEmail(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        emailError: !isValid ? message : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      if (attempts >= 2) {
        setFormData(prev => ({
          ...prev,
          emailError: 'Too many failed attempts. Please try again later.'
        }));
        return;
      }

      // # Clear any existing errors
      setFormData(prev => ({
        ...prev,
        passwordError: '',
        confirmPasswordError: '',
        emailError: ''
      }));

      // # Validate passwords match
      if (formData.password !== formData.confirmPassword) {
        setFormData(prev => ({
          ...prev,
          confirmPasswordError: 'Passwords do not match'
        }));
        return;
      }

      // # Add exit animation before navigation
      if (formSectionRef.current) {
        formSectionRef.current.classList.add('sgnp_mn_signup-form-section-exit');
        await new Promise(resolve => setTimeout(resolve, 300)); // # Wait for animation
      }
      
      // # Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      // # Store credentials in localStorage instead of sessionStorage
      localStorage.setItem('tempPassword', formData.password);
      
      // # Set initial signup step
      localStorage.setItem('lastSignupStep', '2');
      
      // # Create initial user document
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: formData.email,
        emailVerified: false,
        isProfileComplete: false,
        role: 'user',
        signupStep: 2, // # Track signup progress in user document
        created_at: new Date().toISOString(), // # Add created_at at root level
        metadata: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      });
      
      // # Navigate to personal info step
      navigate('/signup/personal');
    } catch (error) {
      console.error('Error in first step:', error);
      
      // Increment attempts
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      sessionStorage.setItem('signupAttempts', newAttempts.toString());

      if (newAttempts >= 2) {
        // Delete user if exists
        if (auth.currentUser) {
          await auth.currentUser.delete();
        }
        setFormData(prev => ({
          ...prev,
          emailError: 'Too many failed attempts. Please try again later.'
        }));
        return;
      }

      let errorMessage = '';
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email already exists';
          setFormData(prev => ({ ...prev, emailError: errorMessage }));
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address';
          setFormData(prev => ({ ...prev, emailError: errorMessage }));
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters';
          setFormData(prev => ({ ...prev, passwordError: errorMessage }));
          break;
        default:
          errorMessage = 'An error occurred. Please try again';
          setFormData(prev => ({ ...prev, emailError: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <AuthHeader />
      <div 
        className="sgnp_mn_signup-container sgnp_mn_signup-main-step"
        style={{ '--signup-background-image': `url(${signupImage})` }}
      >
        <div className="sgnp_mn_signup-image-section" />
        <div className="sgnp_mn_signup-form-section" ref={formSectionRef}>
          <h2>Create Account</h2>
          <form onSubmit={handleSubmit}>
            <div className="sgnp_mn_form-group">
              <div className="sgnp_mn_form-group-title">Account Information</div>
              <div className="sgnp_mn_input-group">
                <label className="sgnp_mn_input-label sgnp_mn_required-field">Email</label>
                <input 
                  type="email" 
                  name="email" 
                  placeholder="Enter your email address"
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />
                {formData.emailError && (
                  <div className="sgnp_mn_error-message">{formData.emailError}</div>
                )}
              </div>
              <div className="sgnp_mn_form-group">
                <div className="sgnp_mn_input-group">
                  <label className="sgnp_mn_input-label sgnp_mn_required-field">Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    className={formData.passwordError ? 'sgnp_mn_error' : ''}
                    required
                  />
                  {formData.passwordError && (
                    <div className="sgnp_mn_error-message">{formData.passwordError}</div>
                  )}
                </div>

                <div className="sgnp_mn_input-group">
                  <label className="sgnp_mn_input-label sgnp_mn_required-field">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={formData.confirmPasswordError ? 'sgnp_mn_error' : ''}
                    required
                  />
                  {formData.confirmPasswordError && (
                    <div className="sgnp_mn_error-message">{formData.confirmPasswordError}</div>
                  )}
                </div>
              </div>
            </div>
            <button 
              type="submit" 
              className="sgnp_mn_submit-button" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <LoadingSpinner />
                  Processing...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SignupMain; 